export type MenuPreviewModeInput = {
  guest_browse_from_route: boolean;
  customer_id: number | undefined;
  is_loading_subscriptions: boolean;
  active_subscription_count: number;
  /** Dev probe — overrides active count for preview gate when probe modes 2/3 are active. */
  menu_eligible_subscription_count?: number;
};

export type MenuPreviewMode = {
  is_logged_in: boolean;
  /** Logged-out guest browsing from Main. */
  is_guest_from_main: boolean;
  /** Logged-in with no active subscription. */
  is_menu_preview_no_subscription: boolean;
  /** View-only week sample — guests or logged-in without a plan. */
  preview_week_dates_mode: boolean;
  /** Sticky back footer on Main guest browse only. */
  show_guest_back_footer: boolean;
  /** Use public guest menu API (`customer_id` 0). */
  guest_menu_preview: boolean;
  should_wait_for_subscriptions: boolean;
};

export function resolveMenuPreviewMode(input: MenuPreviewModeInput): MenuPreviewMode {
  const is_logged_in = typeof input.customer_id === 'number';
  const is_guest_from_main = input.guest_browse_from_route && !is_logged_in;
  const menu_count =
    input.menu_eligible_subscription_count != null
      ? input.menu_eligible_subscription_count
      : input.active_subscription_count;
  const is_menu_preview_no_subscription =
    is_logged_in && !input.is_loading_subscriptions && menu_count === 0;
  const preview_week_dates_mode = is_guest_from_main || is_menu_preview_no_subscription;

  return {
    is_logged_in,
    is_guest_from_main,
    is_menu_preview_no_subscription,
    preview_week_dates_mode,
    show_guest_back_footer: is_guest_from_main,
    guest_menu_preview: preview_week_dates_mode,
    should_wait_for_subscriptions: is_logged_in && input.is_loading_subscriptions,
  };
}

/** Applies dev subscription-menu probe eligibility when resolving preview mode. */
export function resolveMenuPreviewModeWithProbe(args: {
  guest_browse_from_route: boolean;
  customer_id: number | undefined;
  is_loading_subscriptions: boolean;
  subscriptions: Array<{ status?: string }>;
  active_subscription_count: number;
}): MenuPreviewMode {
  let menu_eligible = args.active_subscription_count;
  try {
    const { resolveMenuEligibleSubscriptionCount } =
      require('../data/domains/subscription/hooks') as typeof import('../data/domains/subscription/hooks');
    menu_eligible = resolveMenuEligibleSubscriptionCount(args.subscriptions);
  } catch {
    menu_eligible = args.active_subscription_count;
  }
  return resolveMenuPreviewMode({
    guest_browse_from_route: args.guest_browse_from_route,
    customer_id: args.customer_id,
    is_loading_subscriptions: args.is_loading_subscriptions,
    active_subscription_count: args.active_subscription_count,
    menu_eligible_subscription_count: menu_eligible,
  });
}

/** State 2 probe — block selection when menu_builders GET returned empty for this date. */
export function shouldDisableMenuSelectionForProbe(date_raw: string, preview_week_dates_mode: boolean): boolean {
  if (preview_week_dates_mode) {
    return true;
  }
  try {
    const { getSubscriptionMenuProbeModeSync } =
      require('../exDev/flags/subscriptionMenuProbe') as typeof import('../exDev/flags/subscriptionMenuProbe');
    if (getSubscriptionMenuProbeModeSync() !== 'per_day') {
      return false;
    }
    const { isPerDayMenuSelectionAllowed } =
      require('../exDev/probes/subscriptionMenuProbe/perDayGate') as typeof import('../exDev/probes/subscriptionMenuProbe/perDayGate');
    const date_ymd = String(date_raw ?? '').trim().slice(0, 10);
    return !isPerDayMenuSelectionAllowed(date_ymd);
  } catch {
    return false;
  }
}
